import { getActiveCaregiverId, getActiveOfficeId } from '@lib/activeOffice';
import { getDefaulSocketConfig } from '@src/const/socketConfig';
import { useAuthStore } from '@stores/authStore';
import { SocketEvent } from '@therame/shared';
import { defineStore } from 'pinia';
import { ReplaySubject } from 'rxjs';
import { Socket, io } from 'socket.io-client';
import { ref, type Ref } from 'vue';

type ActivityPointSocketStore = {
  socket: Ref<Socket | undefined>;
  isConnected: Ref<boolean>;
  socketEndpoint: string;
  connect: () => Promise<void>;
  activityPointTrigger: ReplaySubject<SocketEvent<'activity-point-received'>>;
  disconnectSocket: () => void;
};

export const useActivityPointSocketStore = defineStore(
  'activityPointSocket',
  (): ActivityPointSocketStore => {
    const authStore = useAuthStore();
    const socketEndpoint = (import.meta.env.VITE_WS_ROOT as string) + '/activity-point';
    const socket = ref<Socket | undefined>();
    const isConnected = ref<boolean>(false);
    const activityPointTrigger = new ReplaySubject<SocketEvent<'activity-point-received'>>();
    const reconnectInterval = ref<ReturnType<typeof setInterval>>();

    const disconnectSocket = () => {
      socket.value?.disconnect();
      isConnected.value = false;
      clearInterval(reconnectInterval.value);
    };

    const connectSocket = async () => {
      return Promise.race([
        new Promise((resolve) => {
          setTimeout(() => {
            resolve(false);
          }, 1000);
        }),
        new Promise<boolean>((resolve) => {
          socket.value = io(
            socketEndpoint,
            getDefaulSocketConfig(
              authStore.authContext?.token,
              getActiveOfficeId(),
              getActiveCaregiverId(),
            ),
          );
          socket.value.on('connect', () => {
            isConnected.value = true;
            resolve(true);
          });
          socket.value.on('disconnect', () => {
            isConnected.value = false;
          });
          socket.value?.on(
            'activity-point-received',
            (event: SocketEvent<'activity-point-received'>) => {
              const activityPointAlert = event.props.activityPointAlert;
              if (activityPointAlert) {
                activityPointTrigger.next(event);
              }
            },
          );
          socket.value.connect();
        }),
      ]);
    };

    const connect = async () => {
      const connected = await connectSocket();
      if (!connected) {
        clearInterval(reconnectInterval.value);
        reconnectInterval.value = setInterval(async () => {
          const connected = await connectSocket();
          if (connected) {
            clearInterval(reconnectInterval.value);
          }
        }, 1000);
      }
    };

    const store: ActivityPointSocketStore = {
      socket,
      isConnected,
      socketEndpoint,
      connect,
      activityPointTrigger,
      disconnectSocket,
    };
    return store;
  },
);
